import { type FC, useEffect, useRef, useState } from "react";
import { ActionIcon, Alert, Grid, Group, Stack } from "@mantine/core";
import Text from "@/components/atoms/Text";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import { ReceptionOpeningHoursDay, ReceptionSlotDuration, ReceptionTimeSlotConfig } from "@/types";
import { SettingsFormContent, SettingsHeader } from "@/components/pages/Settings/SettingsLayout";
import { usePutSlotConfig } from "@/services/ReceptionSettings";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import ReceptionCalendarModal from "./ReceptionCalendarModal";
import styles from "./TimeSlotsSection.module.css";

interface Props {
  companyId: number;
  /** null when the backend has no slot configuration yet (404 — a valid "not configured" state). */
  slotConfig: ReceptionTimeSlotConfig | null;
  openingHours: ReceptionOpeningHoursDay[];
  receiverCompanyId: number;
  isLoading: boolean;
}

const DEFAULT_SLOT_CONFIG: ReceptionTimeSlotConfig = { slotDurationMinutes: 60, customersPerSlot: 2 };

const DURATION_OPTIONS: { label: string; value: string; minutes: number }[] = [
  { label: "30 min", value: "30", minutes: 30 },
  { label: "1 time", value: "60", minutes: 60 },
  { label: "2 timer", value: "120", minutes: 120 },
];

function getRepresentativeDay(openingHours: ReceptionOpeningHoursDay[]): ReceptionOpeningHoursDay | null {
  if (openingHours.length === 0) return null;
  const today = dayjs().day() === 0 ? 7 : dayjs().day();
  return openingHours.find((d) => d.dayOfWeek === today) ?? openingHours[0];
}

function calcDailyStats(
  openingHours: ReceptionOpeningHoursDay[],
  slotDurationMinutes: number,
  customersPerSlot: number
) {
  if (openingHours.length === 0) return null;
  const totalOpenMinutesPerDay =
    openingHours.reduce((sum, d) => {
      const [oh, om] = d.openTime.split(":").map(Number);
      const [ch, cm] = d.closeTime.split(":").map(Number);
      return sum + (ch * 60 + cm - (oh * 60 + om));
    }, 0) / openingHours.length;
  const slotsPerDay = Math.floor(totalOpenMinutesPerDay / slotDurationMinutes);
  return { slotsPerDay, totalCapacity: slotsPerDay * customersPerSlot };
}

function getPreviewSlots(day: ReceptionOpeningHoursDay | null, slotDurationMinutes: number): string[] {
  if (!day) return [];
  const [oh, om] = day.openTime.split(":").map(Number);
  const [ch, cm] = day.closeTime.split(":").map(Number);
  const startMin = oh * 60 + om;
  const endMin = ch * 60 + cm;
  const slots: string[] = [];
  for (let m = startMin; m + slotDurationMinutes <= endMin; m += slotDurationMinutes) {
    const e = m + slotDurationMinutes;
    slots.push(
      `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}–` +
        `${String(Math.floor(e / 60)).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}`
    );
  }
  return slots;
}

const MAX_VISIBLE_SLOTS = 3;
const MIN_CUSTOMERS_PER_SLOT = 1;
const MAX_CUSTOMERS_PER_SLOT = 99;
const CUSTOMERS_PER_SLOT_DEBOUNCE_MS = 500;

const TimeSlotsSection: FC<Props> = ({ companyId, slotConfig, openingHours, receiverCompanyId, isLoading }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isConfigured = slotConfig !== null;
  const effectiveConfig = slotConfig ?? DEFAULT_SLOT_CONFIG;

  const { mutate: saveConfig, isPending: isSavingConfig } = usePutSlotConfig(companyId, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["receptionSlotConfig", companyId] }),
  });

  const representativeDay = getRepresentativeDay(openingHours);
  const durationOption = DURATION_OPTIONS.find((o) => o.minutes === effectiveConfig.slotDurationMinutes);

  // Local, immediately-responsive value for the stepper — the actual save is debounced so a
  // burst of +/- clicks doesn't fire one PUT request (and re-render) per click.
  const [customersPerSlot, setCustomersPerSlotDisplay] = useState(effectiveConfig.customersPerSlot);
  const slotConfigRef = useRef(effectiveConfig);
  slotConfigRef.current = effectiveConfig;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setCustomersPerSlotDisplay(effectiveConfig.customersPerSlot);
  }, [effectiveConfig.customersPerSlot]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const changeCustomersPerSlot = (delta: number) => {
    setCustomersPerSlotDisplay((prev) => {
      const clamped = Math.min(MAX_CUSTOMERS_PER_SLOT, Math.max(MIN_CUSTOMERS_PER_SLOT, prev + delta));
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveConfig({ ...slotConfigRef.current, customersPerSlot: clamped });
      }, CUSTOMERS_PER_SLOT_DEBOUNCE_MS);
      return clamped;
    });
  };

  const [showAllPreviewSlots, setShowAllPreviewSlots] = useState(false);

  const stats = calcDailyStats(openingHours, effectiveConfig.slotDurationMinutes, customersPerSlot);
  const allPreviewSlots = getPreviewSlots(representativeDay, effectiveConfig.slotDurationMinutes);
  const visibleSlots = showAllPreviewSlots ? allPreviewSlots : allPreviewSlots.slice(0, MAX_VISIBLE_SLOTS);
  const remainingSlotsCount = allPreviewSlots.length - visibleSlots.length;

  if (!isConfigured) {
    return (
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Group gap={10}>
              <Text size="md" strong display="technical" tt="uppercase">
                {t("settings:reception.timeSlots.title")}
              </Text>
            </Group>
          </SettingsHeader>
          <Grid.Col span={12}>
            <Alert variant="light" color="gray" radius="sm">
              {t("settings:reception.timeSlots.notConfigured")}
            </Alert>
          </Grid.Col>
          <Grid.Col span={12}>
            <CTA intent="secondary" size="sm" loading={isSavingConfig} onClick={() => saveConfig(DEFAULT_SLOT_CONFIG)}>
              {t("settings:reception.timeSlots.setUp")}
            </CTA>
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    );
  }

  return (
    <>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Group gap={10}>
              <span className={styles.statusBadge}>
                <SVGAsset name="white-checkmark" />
              </span>
              <Text size="md" strong display="technical" tt="uppercase">
                {t("settings:reception.timeSlots.title")}
              </Text>
            </Group>
            <Text size="xs" color="secondary" tt="uppercase">
              {durationOption?.label}
              {" · "}
              {t("settings:reception.timeSlots.customersSummary", { count: customersPerSlot })}
            </Text>
          </SettingsHeader>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text size="sm" color="secondary">
                {t("settings:reception.timeSlots.slotDuration")}
              </Text>
              <SegmentedControls
                data={DURATION_OPTIONS.map(({ label, value }) => ({ label, value }))}
                value={String(effectiveConfig.slotDurationMinutes)}
                disabled={isLoading}
                onChange={(val) =>
                  saveConfig({ ...effectiveConfig, slotDurationMinutes: Number(val) as ReceptionSlotDuration })
                }
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text size="sm" color="secondary">
                {t("settings:reception.timeSlots.customersPerSlot")}
              </Text>
              <Group gap={0} className={styles.stepper}>
                <ActionIcon
                  variant="default"
                  className={styles.stepperButton}
                  disabled={isLoading || customersPerSlot <= MIN_CUSTOMERS_PER_SLOT}
                  onClick={() => changeCustomersPerSlot(-1)}
                  aria-label={t("settings:reception.timeSlots.decreaseCustomers")}
                >
                  −
                </ActionIcon>
                <Text size="sm" className={styles.stepperValue}>
                  {customersPerSlot}
                </Text>
                <ActionIcon
                  variant="default"
                  className={styles.stepperButton}
                  disabled={isLoading || customersPerSlot >= MAX_CUSTOMERS_PER_SLOT}
                  onClick={() => changeCustomersPerSlot(1)}
                  aria-label={t("settings:reception.timeSlots.increaseCustomers")}
                >
                  +
                </ActionIcon>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col span={12}>
            {stats && representativeDay ? (
              <div className={styles.infoBanner}>
                <Text size="sm">
                  <Text span fw={500}>
                    {t("settings:reception.timeSlots.infoBannerBold", {
                      slotsPerDay: stats.slotsPerDay,
                      duration: durationOption?.label,
                      capacity: customersPerSlot,
                    })}
                  </Text>{" "}
                  <Text span color="secondary">
                    {t("settings:reception.timeSlots.infoBannerRegular", {
                      openTime: representativeDay.openTime.slice(0, 5),
                      closeTime: representativeDay.closeTime.slice(0, 5),
                      totalCapacity: stats.totalCapacity,
                    })}
                  </Text>
                </Text>
              </div>
            ) : (
              <Alert variant="light" color="gray" radius="sm">
                {t("settings:reception.timeSlots.noOpeningHours")}
              </Alert>
            )}
          </Grid.Col>

          {visibleSlots.length > 0 && (
            <Grid.Col span={12}>
              <Stack gap="xs">
                <Text size="sm" color="secondary">
                  {t("settings:reception.timeSlots.previewTitle")}
                </Text>
                <Group gap="xs">
                  {visibleSlots.map((slot) => (
                    <div key={slot} className={styles.previewSlot}>
                      <Text size="sm" className={styles.previewSlotTime}>
                        {slot}
                      </Text>
                      <Text className={styles.previewSlotSub}>
                        {t("settings:reception.timeSlots.slotCapacity", { count: customersPerSlot })}
                      </Text>
                    </div>
                  ))}
                  {remainingSlotsCount > 0 && representativeDay && (
                    <button
                      type="button"
                      className={`${styles.previewSlot} ${styles.previewSlotMore} ${styles.previewSlotMoreClickable}`}
                      onClick={() => setShowAllPreviewSlots(true)}
                      data-testid="preview-slots-show-more"
                    >
                      <Text size="sm" className={styles.previewSlotTime}>
                        {t("settings:reception.timeSlots.moreSlots", { count: remainingSlotsCount })}
                      </Text>
                      <Text className={styles.previewSlotSub}>
                        {t("settings:reception.timeSlots.untilClose", {
                          closeTime: representativeDay.closeTime.slice(0, 5),
                        })}
                      </Text>
                    </button>
                  )}
                </Group>
              </Stack>
            </Grid.Col>
          )}
        </Grid>
      </SettingsFormContent>
    </>
  );
};

export default TimeSlotsSection;
